/*
  IT Applications Service
  CRUD for ORG_applications with self-hierarchical parent_application_id.
  Endpoint: /api/v1/org/applications
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

// DB-aligned interface
export interface DBApplication {
  id: number;
  entity_id: number;
  application_code: string;
  application_name: string;
  description?: string;
  category: 'APPLICATION' | 'MODULE' | 'FUNCTION';
  version_number?: string;
  parent_application_id?: number | null;
  vendor_name?: string;
  purchase_date?: string;
  ownership_type?: string; // 'Purchased' | 'Self Developed' | 'Licensed'
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Workflow fields (added by migration 103)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  // Joined name fields returned by list/get queries
  created_by_name?: string;
  submitted_by_name?: string;
  approved_by_name?: string;
  rejected_by_name?: string;
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

export interface ApplicationQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  category?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: { page: number; limit: number; total: number; pages: number };
}

class ApplicationsService {
  private basePath = '/api/v1/org/applications';

  async getAll(params?: ApplicationQueryParams): Promise<DBApplication[]> {
    try {
      const qp = new URLSearchParams();
      if (params?.status) qp.append('status', params.status);
      if (params?.search) qp.append('search', params.search);
      if (params?.category) qp.append('category', params.category);
      if (params?.page) qp.append('page', params.page.toString());
      if (params?.limit) qp.append('limit', params.limit.toString());

      const qs = qp.toString();
      const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
      const response: AxiosResponse<ApiResponse<DBApplication[]>> = await apiClient.get(url);

      if (response.data?.data) return response.data.data;
      throw new Error(response.data?.error || 'Failed to fetch applications');
    } catch (error: any) {
      console.error('Error fetching applications:', error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw new Error(error.response?.data?.detail || error.message || 'Failed to fetch applications');
    }
  }

  async getById(id: number): Promise<DBApplication> {
    const response: AxiosResponse<ApiResponse<DBApplication>> = await apiClient.get(`${this.basePath}/${id}`);
    if (response.data?.data) return response.data.data;
    throw new Error('Not found');
  }

  async create(data: Partial<DBApplication>): Promise<DBApplication> {
    try {
      const response: AxiosResponse<ApiResponse<DBApplication>> = await apiClient.post(this.basePath, data);
      if (response.data?.data) return response.data.data;
      throw new Error(response.data?.error || 'Failed to create');
    } catch (error: any) {
      console.error('Error creating application:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to create application');
    }
  }

  async update(id: number, data: Partial<DBApplication>): Promise<DBApplication> {
    try {
      const response: AxiosResponse<ApiResponse<DBApplication>> = await apiClient.put(`${this.basePath}/${id}`, data);
      if (response.data?.data) return response.data.data;
      throw new Error(response.data?.error || 'Failed to update');
    } catch (error: any) {
      console.error('Error updating application:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to update application');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<null>> = await apiClient.delete(`${this.basePath}/${id}`);
      if (!response.data?.success) throw new Error(response.data?.error || 'Failed to delete');
    } catch (error: any) {
      console.error('Error deleting application:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to delete application');
    }
  }

  async submit(id: number): Promise<void> {
    const res = await apiClient.post<ApiResponse<void>>(`${this.basePath}/${id}/submit`);
    if (!res.data.success) throw new Error(res.data.error || 'Submit failed');
  }

  async approve(id: number, note?: string): Promise<void> {
    const res = await apiClient.post<ApiResponse<void>>(`${this.basePath}/${id}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || 'Approve failed');
  }

  async reject(id: number, reason: string): Promise<void> {
    const res = await apiClient.post<ApiResponse<void>>(`${this.basePath}/${id}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || 'Reject failed');
  }

  async reopen(id: number): Promise<void> {
    const res = await apiClient.post<ApiResponse<void>>(`${this.basePath}/${id}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || 'Reopen failed');
  }

  async getHistory(id: number): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> {
    const response = await apiClient.get<{ items: any[] }>(`${this.basePath}/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    return rows.map((r: any) => ({
      audit_id: r.id,
      entity_id: r.entity_id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by: r.changed_by,
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  }
}

const applicationsService = new ApplicationsService();
export default applicationsService;

// --- Legacy named exports for backward compatibility with older components ---
// (ITApplicationsTab.tsx, ApplicationDetailModal.tsx use these)
export const listApplications = async (params?: { search?: string; type?: string }): Promise<any[]> => {
    return applicationsService.getAll({ search: params?.search }) as any;
};
export const createApplication = async (payload: any): Promise<any> => {
    return applicationsService.create(payload) as any;
};
export const updateApplication = async (id: string, payload: any): Promise<any> => {
    return applicationsService.update(Number(id), payload) as any;
};
export const deleteApplication = async (id: string): Promise<void> => {
    return applicationsService.delete(Number(id));
};
export const getApplication = async (id: string): Promise<any> => {
    return applicationsService.getById(Number(id)) as any;
};
